import { httpinstance } from "../axios/api";
import { isKybApproved } from "./kyb";

// Unverified (KYC pending) customers can raise this many queries / run this
// many rate searches before the search is blocked behind the verify-email modal.
// Air and ocean count together — both are saved as queries, so the backend's
// totals cover any mix (e.g. 2 ocean + 1 air uses all three).
export const MAX_UNVERIFIED_QUERIES = 3;
const QUERY_GATED_ROLES = ["customer", "customer_admin"];

/**
 * True when /user-details reports both an email and an `email_verified_at`
 * timestamp. Blank strings count as missing, so a half-filled record still
 * goes through the query-count check.
 */
export const hasVerifiedEmail = (user) =>
  Boolean(String(user?.email ?? "").trim()) &&
  Boolean(String(user?.email_verified_at ?? "").trim());

/**
 * Asks the query-count API whether this user may run another rate search.
 * Only users whose business verification (KYB) isn't approved are counted —
 * the counts are null for verified users. Blocked as soon as either counter
 * (queries raised or rate searches run) crosses the limit, whichever gets
 * there first. Any failure lets the search through so the gate can never
 * break rate search on its own.
 *
 * @param {Object} user - /user-details `data.user` ({ id, userroles, ... }),
 *   i.e. `state.data.data.data.user` in redux
 * @returns {Promise<boolean>} true when the search must be blocked
 */
export const isQueryLimitReached = async (user) => {
  if (!user?.id || !QUERY_GATED_ROLES.includes(user?.userroles)) return false;
  // Email already verified: nothing to gate, skip the query-count call.
  if (hasVerifiedEmail(user)) return false;
  try {
    const res = await httpinstance.get("query-count", {
      params: { userId: user.id },
    });
    const data = res?.data?.data;
    return (
      !isKybApproved(data?.kyb_verification_status) &&
      // Counts start at 0, so 0, 1, 2 are the three free ones and 3 is blocked.
      (Number(data?.query_count) >= MAX_UNVERIFIED_QUERIES ||
        Number(data?.search_count) >= MAX_UNVERIFIED_QUERIES)
    );
  } catch (err) {
    // console.error("Failed to fetch query count:", err);
    return false;
  }
};
